import { useEffect, useMemo, useRef, useState } from 'react';
import { getCompanyPriceHistory, unwrapCompanyPriceResponse } from '../services/companyPriceService';
import { enrichBars, sliceWindow, toWeeklyBars } from '../utils/technicalIndicators';
import { buildTechnicalReadout, TECHNICAL_FOOTER } from '../utils/technicalReadout';
import TechnicalChart from './TechnicalChart';
import '../styles/PriceChartTab.css';
import '../styles/TechnicalTab.css';

const INTERVALS = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
];
const PERIODS = ['3M', '6M', '1Y'];

function toRows(raw) {
    let result = unwrapCompanyPriceResponse(raw);
    if (Array.isArray(result)) result = { data: result };
    else if (result?.data && !Array.isArray(result.data) && Array.isArray(result.data.data)) result = result.data;
    const rows = Array.isArray(result?.data) ? result.data : [];
    return rows.filter((row) => Number.isFinite(Number(row?.close)));
}

export default function TechnicalTab({ companyId }) {
    const [interval, setInterval] = useState('daily');
    const [period, setPeriod] = useState('6M');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const seqRef = useRef(0);

    useEffect(() => {
        if (!companyId) return undefined;
        const controller = new AbortController();
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        setLoading(true);
        setError(null);
        getCompanyPriceHistory(companyId, '1Y', { signal: controller.signal })
            .then((raw) => {
                if (seq !== seqRef.current) return;
                setRows(toRows(raw));
            })
            .catch((err) => {
                if (seq !== seqRef.current) return;
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setRows([]);
                if (err?.response?.status === 404) return;
                setError(err?.message || 'Failed to load price history');
            })
            .finally(() => {
                if (seq === seqRef.current) setLoading(false);
            });
        return () => controller.abort();
    }, [companyId]);

    const visible = useMemo(() => {
        const enriched = interval === 'weekly' ? enrichBars(toWeeklyBars(rows)) : enrichBars(rows);
        return sliceWindow(enriched, period);
    }, [rows, interval, period]);

    const cards = useMemo(
        () => buildTechnicalReadout(visible[visible.length - 1], { interval }),
        [visible, interval],
    );

    return (
        <div className="technical-tab">
            <div className="technical-toolbar">
                <div className="price-chart-periods" role="group" aria-label="Chart interval">
                    {INTERVALS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={`price-period-btn ${interval === item.id ? 'price-period-btn--active' : ''}`}
                            onClick={() => setInterval(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <div className="price-chart-periods" role="group" aria-label="Visible range">
                    {PERIODS.map((item) => (
                        <button
                            key={item}
                            type="button"
                            className={`price-period-btn ${period === item ? 'price-period-btn--active' : ''}`}
                            onClick={() => setPeriod(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            </div>

            {loading && <div className="price-chart-status">Loading price history…</div>}
            {!loading && error && <div className="price-chart-status">{error}</div>}
            {!loading && !error && rows.length === 0 && (
                <div className="price-chart-status">No daily prices stored for this company yet.</div>
            )}

            {!loading && !error && rows.length > 0 && (
                <>
                    <div className="technical-checklist">
                        {cards.map((card) => (
                            <section key={card.label} className="technical-card">
                                <div className="price-stat-label">{card.label}</div>
                                <div className="price-stat-value">{card.value}</div>
                                {card.details ? card.details.map((line) => (
                                    <p key={line} className="technical-card__sentence">{line}</p>
                                )) : (
                                    <p className="technical-card__sentence">{card.sentence}</p>
                                )}
                            </section>
                        ))}
                    </div>
                    <TechnicalChart rows={visible} interval={interval} />
                    <p className="technical-footer">{TECHNICAL_FOOTER}</p>
                </>
            )}
        </div>
    );
}
